import {
  ActionIcon,
  Box,
  Button,
  Center,
  createStyles,
  Flex,
  Group,
  Menu,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  useMantineTheme,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { ITrackedEquipment, ITrackedEquipmentService } from '../../../../models';
import TrackedEquipmentDetailSection from '../TrackedEquipmentDetailSection';
import { ArrowLeft, Dots, ExclamationMark, Trash } from 'tabler-icons-react';
import { useEffect, useState } from 'react';
import {
  DialogModal,
  InfoButton,
  TrackedEquipmentServiceDetailSection,
  TrackedEquipmentServiceHourSection,
  TrackedEquipmentServiceMetricSection,
  TrackedEquipmentServiceNoteSection,
} from '../../..';
import { useDeleteTrackedEquipmentService, useIsAdmin, useIsTechnician } from '../../../../hooks';

const useStyles = createStyles((theme) => ({
  notesCard: {
    border: `1px solid ${theme.colors.gray[4]}`,
    borderRadius: theme.radius.sm,
    paddingLeft: theme.spacing.sm,
    paddingRight: theme.spacing.sm,
    paddingTop: 3,
    width: '100%',
    height: '125px',
  },
  infoBox: {
    color: `${theme.colors.gray[6]}`,
    border: `1px solid ${theme.colors.gray[6]}`,
    borderRadius: theme.radius.lg,
    width: 'fit-content',
    paddingLeft: '10px',
    paddingRight: '10px',
  },
  groupLabel: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.sm,
    whiteSpace: 'pre',
  },
  fieldLabel: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.xs,
    whiteSpace: 'pre',
  },
  editLabel: {
    fontSize: theme.fontSizes.xs,
    fontWeight: 500,
    whiteSpace: 'pre',
  },
  detail: {
    fontSize: theme.fontSizes.md,
    paddingLeft: 10,
  },
  fieldGroup: {
    width: '48%',
  },
  clickableIcon: {
    color: `${theme.colors.blue[6]}`,
    padding: '0 !important',
  },
  buttonText: {
    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      fontSize: 12,
    },
  },
}));

type TrackedEquipmentServiceDetailModalProps = {
  trackedEquipment: ITrackedEquipment;
  service: ITrackedEquipmentService;
  opened: boolean;
  onClose: () => void;
  onDelete: () => void;
};

const TrackedEquipmentServiceDetailModal = (props: TrackedEquipmentServiceDetailModalProps) => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const isSmallMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  const isAdmin = useIsAdmin().data?.isAdmin;
  const isTechnician = useIsTechnician().data?.isTechnician;

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [isError, setIsError] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');

  const deleteTrackedEquipmentService = useDeleteTrackedEquipmentService();

  const handleDelete = () => {
    deleteTrackedEquipmentService.mutate({
      trackedEquipmentServiceId: props.service.trackedEquipmentServiceId,
    });
  };

  useEffect(() => {
    if (deleteTrackedEquipmentService.isSuccess) {
      props.onDelete();
    }
    if (deleteTrackedEquipmentService.isError) {
      setIsError(true);
      setMessage('Something went wrong, Service not Deleted');
    }
  }, [deleteTrackedEquipmentService.status]);

  return (
    <Modal
      title={<Text color={isError ? 'red' : 'green'}>{message}</Text>}
      opened={props.opened}
      size={isSmallMobile ? 'xs' : 'md'}
      onClose={() => {
        props.onClose();
      }}
    >
      <Stack spacing={0} style={{ position: 'relative' }}>
        <Center>
          <Text size="xl" weight={500} align="center">
            Equipment Service Record
          </Text>
          <Flex
            direction={isSmallMobile ? 'column' : 'row'}
            align="center"
            style={{ position: 'absolute', right: -5 }}
          >
            <InfoButton
              userId={props.service?.lastUpdatedUserId ?? ''}
              date={props.service?.lastUpdatedDateTime ?? new Date()}
              ml={5}
            />
            {(isAdmin || isTechnician) && (
              <Menu>
                <Menu.Target>
                  <ActionIcon>
                    <Dots />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    icon={<Trash color={`${theme.colors.red[6]}`} />}
                    onClick={() => {
                      setDeleteModalOpen(true);
                    }}
                  >
                    Delete Service Record
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            )}
          </Flex>
        </Center>
        <TrackedEquipmentDetailSection trackedEquipment={props.trackedEquipment} readOnly />

        <TrackedEquipmentServiceDetailSection
          service={props.service}
          readOnly={props.trackedEquipment.retired}
          onEdited={() => {
            setIsError(false);
            setMessage('Service Details Successfully Updated!');
          }}
          onError={() => {
            setIsError(true);
            setMessage('Something went wrong, Service Details not Updated');
          }}
        />
        <Group spacing={theme.spacing.xl} align="top" grow noWrap>
          <TrackedEquipmentServiceMetricSection
            trackedEquipmentService={props.service}
            readOnly={props.trackedEquipment.retired}
            onEdited={() => {
              setIsError(false);
              setMessage('Service Metrics Successfully Updated!');
            }}
            onError={() => {
              setIsError(true);
              setMessage('Something went wrong, Service Metrics not Updated');
            }}
          />
          {/**Hours Active */}
          <TrackedEquipmentServiceHourSection
            trackedEquipmentService={props.service}
            readOnly={props.trackedEquipment.retired}
            onEdited={() => {
              setIsError(false);
              setMessage('Hours Active Successfully Updated!');
            }}
            onError={() => {
              setIsError(true);
              setMessage('Something went wrong, Hours Active not Updated');
            }}
          />
        </Group>
        <Box mb={5}>
          <TrackedEquipmentServiceNoteSection
            trackedEquipmentService={props.service}
            readOnly={props.trackedEquipment.retired}
            onEdited={() => {
              setIsError(false);
              setMessage('Equipment Notes Successfully Updated!');
            }}
            onError={() => {
              setIsError(true);
              setMessage('Something went wrong, Equipment Notes not Updated');
            }}
          />
        </Box>

        <Stack spacing={0} mt="xs">
          <Text className={classes.fieldLabel}>Equipment Status</Text>
          <Center>
            <SegmentedControl
              color="blue"
              style={{ border: `1px solid ${theme.colors.gray[4]}`, pointerEvents: 'none' }}
              data={[
                { label: 'Ready for Cleaning', value: 'clean' },
                { label: 'In Need of Repair', value: 'repair' },
              ]}
              defaultValue={props.service.readyForClean ? 'clean' : 'repair'}
              ml={10}
            />
          </Center>
        </Stack>

        <Button
          variant="outline"
          mt="md"
          onClick={() => {
            props.onClose();
          }}
        >
          <ArrowLeft style={{ marginRight: 5 }} />
          Return to Service History
        </Button>
      </Stack>
      <DialogModal
        isOpen={deleteModalOpen}
        isNegative
        titleText="Delete Service Record?"
        body={
          <Text align="center" color="dimmed" size="lg" mb="xs">
            Are you sure you want to delete this Equipment?
          </Text>
        }
        confirmText="Delete Service"
        onConfirm={() => {
          setDeleteModalOpen(false);
          handleDelete();
        }}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
      />
    </Modal>
  );
};

export default TrackedEquipmentServiceDetailModal;
